import { ZONES, LINE_DEFS } from '../constants.js'
import { outerSize } from '../objects/courtGrid.js'
import { pickZonePalette } from '../tiles/zonePalette.js'

// ============================================================================
//  quotePayload — czysta funkcja budująca obiekt `configuration` do wysyłki
//  wyceny. ZERO DOM / THREE / import.meta → w pełni testowalna pod Node.
//
//  UWAGA: świadomie NIE importujemy resolverów z tileLibrary.js — ten moduł ma
//  `import * as THREE` i `const BASE = import.meta.env.BASE_URL` na górze, więc
//  jego import wywala się pod Node. Powielamy tu mikro-resolvery (kilka linii),
//  by builder pozostał samodzielny i testowalny. Wszystkie czytelne wartości
//  (name, material, price, colorName) pochodzą WYŁĄCZNIE z przekazanych JSON-ów.
// ============================================================================

// Etykiety stref koloru wg ZONES (fallback: klucz). Mapa liczona raz przy imporcie.
const ZONE_LABEL = new Map(ZONES.map((z) => [z.key, z.label]))

// Zaokrąglenie do 2 miejsc po przecinku (metry liczone z płytek).
const round2 = (x) => Math.round(x * 100) / 100

// Model po id (fallback do pierwszego) — odpowiednik resolveModel z tileLibrary.
function pickModel(catalog, modelId) {
  return catalog.models.find((m) => m.id === modelId) || catalog.models[0]
}

// Kosz {id,name,offsetTiles,offsetM,position} z katalogu lub null (brak
// katalogu albo brak wyboru). Offset = suwak „Pozycja kosza" (w płytkach;
// dodatni = w głąb boiska); `position` to czytelny opis dla handlowca.
function pickHoop(hoopCatalog, hoopId, offsetTiles, tileMm) {
  if (!hoopCatalog || !hoopId) return null
  const h = hoopCatalog.hoops.find((x) => x.id === hoopId) || hoopCatalog.hoops[0]
  const k = Number.isInteger(offsetTiles) ? offsetTiles : 0
  return {
    id: h.id,
    name: h.name,
    offsetTiles: k,
    offsetM: round2((k * tileMm) / 1000),
    position: k === 0
      ? 'standard (za linią końcową)'
      : k > 0 ? `${k} szt w głąb boiska` : `${-k} szt za linią końcową`,
  }
}

// `notes` (opcjonalny) to treść pola „Uwagi" z modalu wyceny — jedyne miejsce
// ich zbierania. Brak argumentu → fallback do state.notes (zachowany defensywnie;
// w UI już nieużywany) → pusty string.
export function buildQuoteConfiguration({ state, catalog, hoopCatalog, notes, viewUrl }) {
  const model = pickModel(catalog, state.model)
  // Nazwa koloru z palety STREFY (strefy linii mają własny zakres 'court-lines').
  const colorName = (zone, id) => {
    const entry = pickZonePalette(catalog, model.id, zone).find((p) => p.id === id)
    return entry ? entry.name : id
  }
  const tileMm = model.size.width // płytka kwadratowa: width = height

  const edgeMm = model.edge ?? 80 // ten sam fallback co przy budowie płyty (main)
  const outer = outerSize({
    widthTiles: state.widthTiles, lengthTiles: state.lengthTiles, runoffTiles: state.runoffTiles,
    tileMm, edgeMm,
  })

  return {
    model: {
      id: model.id,
      name: model.name,
      material: model.material,
      price: model.price,
      priceUnit: model.priceUnit,
      tileSizeMm: {
        width: model.size.width,
        height: model.size.height,
        thickness: model.size.thickness,
      },
      url: model.url,
    },
    // widthM/lengthM = wymiar ZEWNĘTRZNY boiska z wybiegiem i obrzeżem (realny
    // gabaryt produktu, jak na layoutach; uwaga klienta 04.09.2026 — wcześniej
    // były tu metry samego pola gry). Pole gry osobno (courtWidthM/courtLengthM),
    // obrzeże modelu w mm — handlowiec widzi, z czego składa się gabaryt.
    dimensions: {
      widthTiles: state.widthTiles,
      lengthTiles: state.lengthTiles,
      runoffTiles: state.runoffTiles,
      widthM: round2(outer.widthM),
      lengthM: round2(outer.lengthM),
      courtWidthM: round2((state.widthTiles * tileMm) / 1000),
      courtLengthM: round2((state.lengthTiles * tileMm) / 1000),
      edgeMm,
      lineSpec: state.lineSpec,
      preset: state.preset,
    },
    colors: Object.keys(state.colors).map((zone) => ({
      zone,
      label: ZONE_LABEL.get(zone) || zone,
      colorId: state.colors[zone],
      colorName: colorName(zone, state.colors[zone]),
    })),
    // Linie za 3 pkt niosą flagę `undersized` (promień łuku dociśnięty
    // głębokością boiska — linia umowna, nie regulaminowa; state.lineFit liczy
    // Court przy budowie). Sufiks w labelu, by handlowiec widział to bez
    // znajomości pola.
    lines: LINE_DEFS.map((def) => {
      const isThree = def.key === 'three' || def.key === 'threeJr'
      const undersized = isThree && state.lineFit?.[def.key] === false
      return {
        key: def.key,
        label: def.label + (undersized ? ' — NIEWYMIAROWA (łuk przycięty głębokością boiska)' : ''),
        enabled: !!state.lines[def.key],
        ...(isThree ? { undersized } : {}),
      }
    }),
    hoop: pickHoop(hoopCatalog, state.hoop, state.hoopOffsetTiles, tileMm),
    // Link otwierający konfigurator z tą konfiguracją (#cfg=base64url) —
    // buduje go wywołujący (quoteModal), bo wymaga location.origin.
    viewUrl: viewUrl || null,
    notes: (notes !== undefined ? notes : state.notes) || '',
  }
}
